import { Button, Group, Box, useMantineTheme, Grid } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import {
  InputMaybe,
  Scalars,
  UpdateTimeClockEntryInput,
  useAddTimeClockSessionMutation,
  useGetEmployeesNameQuery,
  useGetEmployeesStationNameManyQuery,
} from "src/generated/graphql";
import { memo } from "react";
import { object, date, string } from "yup";
import DatePicker from "src/components/DatePicker";
import TimeInput from "src/components/TimeInput";
import Select from "src/components/Select";
import {
  TimeDiffType,
  createDateTime,
  getDateDiff,
} from "src/utils/dateHelper";
import { errorNotification } from "src/utils/alerts";
import { useAppContext } from "src/api/AppContext";

type CreateTimeLogFormProps = {
  handleClose: () => void;
  onRefetch: () => void;
  stationsEnabled: boolean;
};
type IFormProps = Omit<UpdateTimeClockEntryInput, "station"> & {
  employee: string;
  startedTime: InputMaybe<Scalars["DateTime"]> | undefined;
  endedTime: InputMaybe<Scalars["DateTime"]> | undefined;
  station?: string;
};

const CreateTimeLogForm = ({
  handleClose,
  onRefetch,
  stationsEnabled,
}: CreateTimeLogFormProps) => {
  const theme = useMantineTheme();
  const { currentLocation } = useAppContext();
  const { data } = useGetEmployeesNameQuery({
    variables: { locationId: currentLocation?.id || "" },
    skip: !currentLocation?.id,
  });

  const form = useForm<IFormProps>({
    initialValues: {
      employee: "",
      endedAt: undefined,
      startedAt: undefined,
      startedTime: undefined,
      endedTime: undefined,
      station: "",
    },
    validate: yupResolver(
      object().shape({
        employee: string().required(),
        startedAt: date().required("Start Date is required"),
        startedTime: date().required("Start Time is required"),
        ...(stationsEnabled && {
          station: string().required("Station is required"),
        }),
      })
    ),
  });

  const _empId = form.getInputProps("employee").value;
  const _stationId = form.getInputProps("station").value;

  const { data: stations } = useGetEmployeesStationNameManyQuery({
    variables: {
      employeeId: _empId,
      locationId: currentLocation?.id || "",
    },
    skip: !_empId || !currentLocation,
    fetchPolicy: "network-only",
    onCompleted(data) {
      if (!data.employeeStationMany && !_stationId) return;
      // reset station if no matching station found
      if (!data.employeeStationMany?.find((s) => s.id === _stationId)) {
        form.setFieldValue("station", "");
      }
    },
  });

  const [createTimeClockSession, { loading }] = useAddTimeClockSessionMutation({
    onCompleted: () => {
      onRefetch();
      form.resetDirty();
      handleClose();
    },
  });

  const handleCreate = async (values: IFormProps) => {
    const { employee, station } = values;
    let _start = createDateTime(values.startedAt, values.startedTime);
    let _end = createDateTime(values.endedAt, values.endedTime);
    let _diff = getDateDiff(_start, _end, TimeDiffType.seconds);

    if (_end && _diff < 0) {
      return errorNotification({
        message: "Start Time must be less than End",
      });
    }
    try {
      await createTimeClockSession({
        variables: {
          ...(station && { station }),
          employeeId: employee,
          startDate: _start,
          endDate: _end,
        },
      });
    } catch (e: any) {
      console.error("Error: ", e.response);
    }
  };

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <form
        onSubmit={form.onSubmit((values) => {
          handleCreate(values);
        })}
      >
        <Grid>
          <Grid.Col span={12}>
            <Select
              data={
                data?.employeeMany?.map((val) => ({
                  value: val?.id,
                  label: `${val?.user?.firstName}  ${val?.user?.lastName}`,
                })) || []
              }
              label="Select Employee"
              {...form.getInputProps("employee")}
            />
            {stationsEnabled && (
              <Select
                data={
                  stations?.employeeStationMany?.map((val) => ({
                    value: val.id,
                    label: val.name,
                  })) || []
                }
                label="Select Station"
                {...form.getInputProps("station")}
              />
            )}
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={6}>
            <DatePicker
              label="Start Date"
              maw={400}
              mx="auto"
              {...form.getInputProps("startedAt")}
              disabled={loading}
            />
          </Grid.Col>

          <Grid.Col span={6} p={5}>
            <TimeInput
              // withSeconds
              label="Start Time"
              {...form.getInputProps("startedTime")}
              disabled={loading}
              format="12"
            />
          </Grid.Col>
        </Grid>

        <Grid justify="space-between">
          <Grid.Col span={6}>
            <DatePicker
              label="End Date"
              maw={400}
              mx="auto"
              {...form.getInputProps("endedAt")}
              disabled={loading}
            />
          </Grid.Col>

          <Grid.Col span={6}>
            <TimeInput
              // withSeconds
              label="End Time"
              {...form.getInputProps("endedTime")}
              disabled={loading}
              format="12"
            />
          </Grid.Col>
        </Grid>

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loading}
          >
            Create
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default memo(CreateTimeLogForm);
